import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { placeOrder } from '../api';
import { useCart } from '../CartContext';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart(); // Add clearCart from context
  const [status, setStatus]     = useState('verifying');
  const [orderNum, setOrderNum] = useState('');
  const [txnData, setTxnData]   = useState({});
  const [orderData, setOrderData] = useState(null); // full order for summary

  const urlStatus = searchParams.get('status') || '';
  const txnId     = searchParams.get('txn')    || '';
  const mode      = searchParams.get('mode')   || '';
  const amount    = searchParams.get('amount') || '';

  // ── Same logic as before ──────────────────────────────────
  useEffect(() => {
    if (urlStatus === 'failed') { setStatus('failed'); return; }

    if (urlStatus === 'success') {
      const pendingRaw   = sessionStorage.getItem('pendingOrder');
      const snapshotRaw  = sessionStorage.getItem('pendingCartSnapshot');

      if (pendingRaw) {
        try {
          const pending  = JSON.parse(pendingRaw);
          const snapshot = snapshotRaw ? JSON.parse(snapshotRaw) : null;

          const modeMap = {
            'credit card': 'card', 'debit card': 'card', 'card': 'card',
            'upi': 'upi', 'upi intent': 'upi',
            'wallet': 'wallet',
            'net banking': 'netbanking', 'netbanking': 'netbanking', 'nb': 'netbanking',
          };
          const normalizedMode = modeMap[(mode || '').toLowerCase()] || 'card';
          pending.payment_method = normalizedMode;
          pending.notes = (pending.notes || '') + ` | TXN:${txnId}`;

          placeOrder(pending)
            .then(res => {
              sessionStorage.removeItem('pendingOrder');
              sessionStorage.removeItem('pendingCartSnapshot');
              clearCart(); // Clear cart after successful order placement
              setOrderNum(res.order?.order_number || '');
              setTxnData({ transaction_id: txnId, payment_mode: mode, amount });
              setOrderData({ pending, snapshot });
              setStatus('success');
            })
            .catch(() => {
              // Even if API fails, payment was successful, so clear cart
              clearCart();
              setTxnData({ transaction_id: txnId, payment_mode: mode, amount });
              setOrderData({ pending, snapshot });
              setStatus('success');
            });
        } catch {
          setTxnData({ transaction_id: txnId, payment_mode: mode, amount });
          setStatus('success');
        }
      } else {
        setTxnData({ transaction_id: txnId, payment_mode: mode, amount });
        setStatus('success');
      }
    }
  }, [urlStatus, txnId, mode, amount]);

  // ── Print receipt ─────────────────────────────────────────
  const handlePrint = () => {
    const pw = window.open('', '_blank', 'width=800,height=600');
    if (!pw) return;
    const items   = orderData?.snapshot?.items || [];
    const cart    = orderData?.snapshot?.cartTotal || 0;
    const del     = orderData?.snapshot?.deliveryCharge || 0;
    const total   = orderData?.snapshot?.finalTotal || parseFloat(amount) || 0;
    const pending = orderData?.pending || {};

    pw.document.write(`<!DOCTYPE html><html><head><title>Receipt - ${orderNum}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:sans-serif;padding:30px;background:#fff}
    .r{max-width:520px;margin:auto;border:1px solid #e5e7eb;border-radius:12px;padding:24px}
    h1{text-align:center;color:#16a34a;font-size:1.4rem;margin-bottom:4px}
    .sub{text-align:center;color:#6b7280;font-size:0.85rem;margin-bottom:20px}
    .oid{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:8px 16px;text-align:center;margin-bottom:20px;font-weight:700;color:#15803d}
    .sec{border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin-bottom:16px}
    .sec h3{font-size:0.9rem;font-weight:700;color:#111827;margin-bottom:12px;padding-bottom:6px;border-bottom:1px solid #f3f4f6}
    .item{display:flex;gap:10px;margin-bottom:10px;align-items:center}
    .item img{width:44px;height:44px;object-fit:cover;border-radius:6px;border:1px solid #e5e7eb}
    .item-name{font-size:0.8rem;font-weight:600;color:#111827}
    .item-qty{font-size:0.72rem;color:#9ca3af}
    .item-price{margin-left:auto;font-weight:700;font-size:0.8rem;color:#111827}
    .row{display:flex;justify-content:space-between;font-size:0.82rem;color:#6b7280;padding:3px 0}
    .total{display:flex;justify-content:space-between;font-weight:800;font-size:0.95rem;color:#15803d;border-top:2px solid #bbf7d0;padding-top:8px;margin-top:6px}
    .txn{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 14px;margin-bottom:16px}
    .txn .row{color:#1e40af}
    .footer{text-align:center;font-size:0.72rem;color:#9ca3af;margin-top:16px;padding-top:12px;border-top:1px dashed #e5e7eb}
    @media print{body{padding:0}.r{border:none;box-shadow:none}}</style></head><body>
    <div class="r">
      <h1>Payment Successful 🎉</h1>
      <div class="sub">Thank you for shopping with ePayhub Store</div>
      <div class="oid">Order ID: ${orderNum || '—'}</div>
      <div class="txn">
        <div class="row"><span>Transaction ID</span><span>${txnId || '—'}</span></div>
        <div class="row"><span>Payment Mode</span><span>${mode || '—'}</span></div>
        <div class="row"><span>Amount Paid</span><span>₹${amount || total.toFixed(2)}</span></div>
      </div>
      ${items.length ? `<div class="sec"><h3>Items Purchased</h3>${items.map(i => `
        <div class="item">
          <img src="${i.image || ''}" alt="${i.name}" onerror="this.style.display='none'"/>
          <div><div class="item-name">${i.name}</div><div class="item-qty">Qty: ${i.qty} × ₹${i.price}</div></div>
          <div class="item-price">₹${(i.price * i.qty).toFixed(2)}</div>
        </div>`).join('')}
        <div class="row" style="margin-top:8px"><span>Item Total (${items.length} item${items.length !== 1 ? 's' : ''})</span><span>₹${cart.toFixed(2)}</span></div>
        <div class="row"><span>Delivery Charges</span><span style="color:${del > 0 ? '#374151' : '#16a34a'}">${del > 0 ? '₹' + del.toFixed(2) : 'FREE'}</span></div>
        <div class="row"><span>GST</span><span>₹0.00</span></div>
        <div class="total"><span>Grand Total</span><span>₹${total.toFixed(2)}</span></div>
      </div>` : ''}
      ${pending.address ? `<div class="sec"><h3>Delivery Address</h3>
        <div style="font-size:0.82rem;color:#374151;line-height:1.6">
          <div><strong>${pending.full_name || ''}</strong></div>
          <div>${pending.address || ''}</div>
          <div>${pending.city || 'Hyderabad'} - ${pending.pincode || ''}</div>
          <div>📞 ${pending.phone || ''}</div>
        </div></div>` : ''}
      <div class="footer">System generated receipt · No signature required<br/>Support: kilomart685@gmail.com</div>
    </div>
    <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}<\/script>
    </body></html>`);
    pw.document.close();
  };

  const items    = orderData?.snapshot?.items || [];
  const cart     = orderData?.snapshot?.cartTotal || 0;
  const del      = orderData?.snapshot?.deliveryCharge || 0;
  const total    = orderData?.snapshot?.finalTotal || parseFloat(amount) || 0;
  const pending  = orderData?.pending || {};

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '2rem 1rem' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes scaleUp{from{transform:scale(0);opacity:0}to{transform:scale(1);opacity:1}}
        @keyframes fadeInUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideInRight{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
      `}</style>

      {/* ── Processing ── */}
      {status === 'verifying' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, border: '4px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1.25rem' }} />
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>Processing...</h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Placing your order, please wait.</p>
          </div>
        </div>
      )}

      {/* ── Success — same UI as COD ConfirmationStep ── */}
      {status === 'success' && (
        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>

          {/* Check icon */}
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#f0fdf4', border: '3px solid #16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', animation: 'scaleUp 0.5s ease-out' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" strokeWidth="3" stroke="#16a34a" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/>
            </svg>
          </div>

          <div style={{ animation: 'fadeInUp 0.5s ease-out' }}>
            <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827', marginBottom: '8px' }}>Payment Successful! 🎉</h2>
            <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>Thank you, <strong>{pending.full_name || 'Customer'}</strong>!</p>
          </div>

          {/* Order ID */}
          <div style={{ display: 'inline-block', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '10px 24px', marginBottom: '1.5rem', animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>
            <span style={{ fontWeight: 700, color: '#15803d', fontSize: '1rem' }}>Order ID: {orderNum || '—'}</span>
          </div>

          {/* Order summary card */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.5rem', textAlign: 'left', background: '#fff', animation: 'fadeInUp 0.5s ease-out 0.2s both', marginBottom: '1.5rem' }}>

            {/* Transaction info */}
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 14px', marginBottom: '1.25rem' }}>
              <h4 style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e40af', marginBottom: '8px' }}>Payment Details</h4>
              {[
                ['Transaction ID', txnData.transaction_id || '—'],
                ['Payment Mode',   txnData.payment_mode  || '—'],
                ['Amount Paid',    txnData.amount ? `₹${txnData.amount}` : `₹${total.toFixed(2)}`],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: '4px' }}>
                  <span style={{ color: '#6b7280' }}>{l}</span>
                  <span style={{ fontWeight: 600, color: '#111827' }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Items */}
            {items.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <h4 style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: '12px' }}>Items Purchased:</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {items.map((item, idx) => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: '#f9fafb', borderRadius: '10px', animation: `slideInRight 0.4s ease-out ${0.3 + idx * 0.08}s both` }}>
                      <img src={item.image} alt={item.name} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}{item.unit ? ` (${item.unit})` : ''}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{item.category}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Qty: {item.qty} × ₹{item.price}</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#111827', flexShrink: 0 }}>₹{(item.price * item.qty).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Delivery address */}
            {pending.address && (
              <div style={{ marginBottom: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: '10px' }}>Delivery Address:</h4>
                <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.6 }}>
                  <div><strong>{pending.full_name}</strong></div>
                  <div>{pending.address}</div>
                  <div>{pending.city || 'Hyderabad'} - {pending.pincode}</div>
                  <div>📞 {pending.phone}</div>
                </div>
              </div>
            )}

            {/* Price summary */}
            <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '1rem' }}>
              {[
                ['Item Total (' + items.length + ' item' + (items.length !== 1 ? 's' : '') + ')', `₹${cart.toFixed(2)}`,                                    '#374151', 500],
                ['Delivery Charges',  del > 0 ? `₹${del.toFixed(2)}` : 'FREE',                  del > 0 ? '#374151' : '#16a34a', 600],
                ['GST',               '₹0.00',                                                   '#374151', 400],
              ].map(([l, v, c, w]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '6px' }}>
                  <span style={{ color: '#6b7280' }}>{l}</span>
                  <span style={{ color: c, fontWeight: w }}>{v}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', marginTop: '4px', borderTop: '2px solid #bbf7d0', fontWeight: 800, fontSize: '1rem', color: '#15803d' }}>
                <span>Grand Total:</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
            <button onClick={handlePrint}
              style={{ padding: '11px 22px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}>
              🖨 Print Receipt
            </button>
            <button onClick={() => navigate('/my-orders')}
              style={{ padding: '11px 22px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
              onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}>
              View Orders
            </button>
          </div>
        </div>
      )}

      {/* ── Failed ── */}
      {status === 'failed' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div style={{ background: 'white', borderRadius: '16px', maxWidth: '420px', width: '100%', padding: '2.5rem 2rem', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#fee2e2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6l-12 12"/><path d="M6 6l12 12"/>
              </svg>
            </div>
            <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.2rem', fontWeight: 800, color: '#111827' }}>Payment Failed</h2>
            <p style={{ margin: '0 0 1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Payment was not completed. Your order has not been placed.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => navigate('/checkout')}
                style={{ flex: 1, padding: '0.7rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                Try Again
              </button>
              <button onClick={() => navigate('/')}
                style={{ flex: 1, padding: '0.7rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                Go Home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
